<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref } from 'vue';
  import { Chart as ChartJS, ArcElement, Tooltip, Legend, type Color, type TooltipItem } from 'chart.js';
  import DoughnutChart from '@/views/details/components/influencer-insights/DoughnutChart.vue';

  ChartJS.register(ArcElement, Tooltip, Legend);

  const props = defineProps<{
    gender: {
      male: number;
      female: number;
    };
  }>();

  const doughnutChartRef = ref<any>(null);

  const hasData = computed(() => {
    return !!(props.gender.male || props.gender.female);
  });

  const genderData = computed(() => ({
    labels: ['Female', 'Male'],
    datasets: [
      {
        data: [props.gender.female, props.gender.male],
        backgroundColor: ['#3b82f6', '#14b8a6'],
        hoverOffset: 4,
      },
    ],
  }));

  const doughnutChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    rotation: 225,
    elements: {
      arc: {
        borderWidth: 4,
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#333',
          usePointStyle: true,
          pointStyle: 'rectRounded',
          padding: 16,
          font: {
            size: 14,
          },
        },
      },
      tooltip: {
        backgroundColor: '#1E1B39',
        yAlign: 'bottom',
        padding: {
          top: 10,
          right: 30,
          bottom: 10,
          left: 10,
        },
        bodySpacing: 4,
        cornerRadius: 8,
        callbacks: {
          title: () => {
            return '';
          },
          label: (tooltipItem: TooltipItem<'doughnut'>) => {
            return ` ${tooltipItem.formattedValue}%`;
          },
          labelColor: function (tooltipItem: TooltipItem<'doughnut'>) {
            const backgroundColor = tooltipItem.dataset.backgroundColor;
            const color = (Array.isArray(backgroundColor) ? backgroundColor[tooltipItem.dataIndex] : backgroundColor) as Color;

            return {
              borderColor: color,
              backgroundColor: color,
              borderWidth: 3,
              borderRadius: 3,
            };
          },
        },
      },
    },
  };

  let resizeObserver: ResizeObserver | null = null;

  onMounted(() => {
    // Handle chart resize when container size changes
    const chartContainer = doughnutChartRef.value?.$el?.parentElement;
    if (chartContainer) {
      resizeObserver = new ResizeObserver(() => {
        if (doughnutChartRef.value?.chart) {
          doughnutChartRef.value.chart.resize();
        }
      });
      resizeObserver.observe(chartContainer);
    }
  });

  onUnmounted(() => {
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
  });
</script>

<template>
  <div class="report-age-chart-container flex flex-col gap-24">
    <h2 class="chart-heading">Audience by Gender</h2>
    <div class="chart-wrapper">
      <DoughnutChart v-if="hasData" ref="doughnutChartRef" class="doughnut" :data="genderData" :options="doughnutChartOptions" />
      <div v-else class="no-data-state">
        <p>No gender data available</p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #64748b;
  }

  .no-data-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 250px;
    color: #64748b;
    font-size: 16px;

    @media screen and (min-width: 768px) {
      min-height: 300px;
    }

    @media screen and (min-width: 1024px) {
      min-height: 350px;
    }
  }

  .report-age-chart-container {
    background-color: white;
    padding: 16px;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
    display: flex;
    flex-direction: column;
    height: 100%;

    .chart-heading {
      font-size: 20px;
      font-weight: 600;
      flex-shrink: 0;

      @media screen and (min-width: 768px) {
        font-size: 24px;
      }
    }
  }

  .chart-wrapper {
    position: relative;
    width: 100%;
    flex: 1;
    min-height: 250px;
    display: flex;
    align-items: center;
    justify-content: center;

    @media screen and (min-width: 768px) {
      min-height: 300px;
    }

    @media screen and (min-width: 1024px) {
      min-height: 350px;
    }

    canvas {
      max-height: 100%;
      max-width: 100%;
    }
  }
</style>
